import React, { useEffect, useState } from "react";
import { fetchPromoCoupons, markCouponUsed, redeemCoupon } from "../../services/promotionApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const statusLabel = (c) => {
  if (c.isUsed) return "Used";
  if (c.expired) return "Expired";
  return "Active";
};

const statusClass = (c) => {
  if (c.isUsed) return "badge-inactive";
  if (c.expired) return "badge-inactive";
  return "badge-active";
};

const PromoCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ userId: "", couponCode: "", adminNote: "" });
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setCoupons(await fetchPromoCoupons());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleMarkUsed = async (id) => {
    const note = window.prompt("Redemption note (optional):") || "";
    try {
      await markCouponUsed(id, note);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const userId = Number(redeemForm.userId);
    if (!userId || !redeemForm.couponCode.trim()) {
      setError("User ID and coupon code are required");
      return;
    }
    setRedeeming(true);
    try {
      await redeemCoupon({
        userId,
        couponCode: redeemForm.couponCode.trim().toUpperCase(),
        adminNote: redeemForm.adminNote.trim() || undefined,
      });
      setRedeemForm({ userId: "", couponCode: "", adminNote: "" });
      setError("");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="admin-page">
      <h2>Promo coupons</h2>
      <p className="subtitle">
        Coupons are stored per user when they win a lucky draw. At redemption, ask the customer for their
        <strong> User ID</strong> and <strong>coupon code</strong> — both are required to validate.
      </p>
      {error && <p className="error-msg">{error}</p>}

      <div className="admin-card">
        <h3>Redeem coupon</h3>
        <form className="admin-filters" onSubmit={handleRedeem}>
          <label className="field-label">
            User ID
            <input
              type="number"
              min={1}
              value={redeemForm.userId}
              onChange={(e) => setRedeemForm({ ...redeemForm, userId: e.target.value })}
              placeholder="e.g. 42"
              required
            />
          </label>
          <label className="field-label">
            Coupon code
            <input
              value={redeemForm.couponCode}
              onChange={(e) => setRedeemForm({ ...redeemForm, couponCode: e.target.value })}
              placeholder="SPIN40"
              required
            />
          </label>
          <label className="field-label">
            Note (optional)
            <input
              value={redeemForm.adminNote}
              onChange={(e) => setRedeemForm({ ...redeemForm, adminNote: e.target.value })}
              placeholder="In-store redemption"
            />
          </label>
          <button type="submit" className="admin-btn" disabled={redeeming}>
            {redeeming ? "Validating…" : "Validate & redeem"}
          </button>
        </form>
      </div>

      <div className="admin-card">
        {loading ? <div className="admin-loading">Loading…</div> : (
          <TableScroll>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Code</th>
                  <th>Offer</th>
                  <th>Campaign</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 && <tr><td colSpan={7} className="empty-msg">No coupons yet</td></tr>}
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td>{c.userName} (#{c.userId})</td>
                    <td><strong>{c.couponCode}</strong></td>
                    <td>{c.offerText}</td>
                    <td>{c.campaignTitle}</td>
                    <td>{formatDate(c.expiresAt)}</td>
                    <td className={statusClass(c)}>{statusLabel(c)}</td>
                    <td>
                      {!c.isUsed && !c.expired && (
                        <button type="button" className="admin-btn" onClick={() => handleMarkUsed(c.id)}>
                          Mark used
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </div>
    </div>
  );
};

export default PromoCoupons;
